"use client";

import type { Nav, SubNav } from "@/config/const-orgs";
import { useOrgStore } from "@/store/org-store";
import { ChevronDown, type LucideIcon } from "lucide-react";
import { Protect } from "./auth/protect";


import { Link, useLocation } from "@tanstack/react-router";
import type React from "react";
import { memo, useMemo } from "react";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
	Sidebar,
	SidebarContent,
	SidebarGroup,
	SidebarGroupContent,
	SidebarGroupLabel,
	SidebarHeader,
	SidebarMenu,
	SidebarMenuButton,
	SidebarMenuItem,
	SidebarMenuSub,
	SidebarMenuSubButton,
	SidebarMenuSubItem,
	SidebarRail,
	SidebarFooter,
	cn,
	useSidebar,
} from "shadcn-ui";

import { NavUser } from "./sidebar-footer";
import { VersionSwitcher } from "./sidebar-switcher";

import { nav, orgs } from "@/config/const-orgs";

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
	const currentOrg = useOrgStore((state) => state.currentOrg);
	// const cc = useObservable(c);
	// const nn = useObservable(n);
	// const currentOrg = use$(cc);
	// const nav = use$(nn);

	// const nav = nav[currentOrg.key];
	// const currentOrg = orgs[0];

	// Safely derive the current org's nav list and memoize to avoid re-renders
	const navItems = useMemo(() => {
		const key = currentOrg?.key as string | undefined;
		const list = key ? nav[key] : undefined;
		return Array.isArray(list) ? list : [];
	}, [currentOrg?.key]);


	return (
		<Sidebar collapsible="icon" {...props}>
			<SidebarHeader>
				<VersionSwitcher />
			</SidebarHeader>
			<SidebarContent>
				{navItems.length > 0 ? <NavItems nav={navItems} /> : null}
			</SidebarContent>
			<SidebarFooter>
				<NavUser />
			</SidebarFooter>
			<SidebarRail />
		</Sidebar>
	);
}

/**
 * Checks if the given href exactly matches the current path
 */
const isActiveChecker = (href: string, currentPath: string): boolean => {
	const [cleanHref] = href.split(/[?#]/, 1);
	const [cleanCurrentPath] = currentPath.split(/[?#]/, 1);
	const normalizedHref = cleanHref.replace(/\/+$/, "") || "/";
	const normalizedCurrent = cleanCurrentPath.replace(/\/+$/, "") || "/";
	return normalizedHref === normalizedCurrent;
};


const NavPermission = ({
	children,
	item,
}: { children: React.ReactNode; item?: Nav }) => {
	// if (item?.role) {
	// 	return <Protect role={item.role}>{children}</Protect>;
	// }
	// if (item?.permission) {
	// 	return <Protect permission={item.permission}>{children}</Protect>;
	// }
	return <>{children}</>;
};

// Optimized NavItems with memoization and grouping
const NavItems = memo(({ nav }: { nav: Nav[] }) => {
	const { groupedNav, hasGroups } = useMemo(() => {
		const filtered = nav?.filter((item) => !item.hidden) || [];

		// Group items by category if they have one, otherwise put in default group
		const grouped = filtered.reduce((acc, item) => {
			const group = 'main';
			if (!acc[group]) acc[group] = [];
			acc[group].push(item);
			return acc;
		}, {} as Record<string, Nav[]>);

		return {
			groupedNav: grouped,
			hasGroups: Object.keys(grouped).length > 1 || (Object.keys(grouped)[0] !== 'main')
		};
	}, [nav]);

	if (!Object.keys(groupedNav).length) return <div />;

	return (
		<>
			{Object.entries(groupedNav).map(([groupName, items]) => (
				<SidebarGroup key={groupName}>
					{hasGroups && groupName !== 'main' && (
						<SidebarGroupLabel className="text-xs font-medium text-sidebar-foreground/70">
							{groupName.charAt(0).toUpperCase() + groupName.slice(1)}
						</SidebarGroupLabel>
					)}
					<SidebarGroupContent>
						<SidebarMenu>
							{items.map((item, idx) => (
								<NavItem key={`${groupName}-${item.title}-${idx}`} item={item} depth={0} />
							))}
						</SidebarMenu>
					</SidebarGroupContent>
				</SidebarGroup>
			))}
		</>
	);
});

const NavItemBase = ({ item, depth = 0 }: { item: SubNav; depth?: number }) => {
	const { open } = useSidebar();
	const location = useLocation();
	const currentPath = location.pathname;
	const Icon = item.icon as unknown as LucideIcon;

	const hasChildren = item.items && Array.isArray(item.items) && item.items.length > 0;
	const filteredChildren = useMemo(() =>
		item.items?.filter((child) => !child.hidden) || [],
		[item.items]
	);

	// For parent items with children - handle up to 3 levels
	if (hasChildren) {
		// Level 0: Top level with SidebarMenuItem
		// Level 1: First nested level with SidebarMenuSubItem  
		// Level 2+: Deeper nesting with continued SidebarMenuSubItem
		const MenuContainer = depth === 0 ? SidebarMenuItem : SidebarMenuSubItem;
		const MenuButtonComponent = depth === 0 ? SidebarMenuButton : SidebarMenuSubButton;

		// Determine if any descendant (or self) matches the current route
		const isActiveInTree = useMemo(() => {
			const check = (node: SubNav): boolean => {
				const selfActive = node.url ? isActiveChecker(node.url, currentPath) : false;
				if (selfActive) return true;
				if (node.items && Array.isArray(node.items)) {
					return node.items.some(check);
				}
				return false;
			};
			return check(item);
		}, [item, currentPath]);

		return (
			<NavPermission item={item}>
				<Collapsible
					defaultOpen={isActiveInTree || (depth === 0 ? open : false)}
					className={cn("group/collapsible", `group-collapsible-${depth}`)}
				>
					<MenuContainer>
						<CollapsibleTrigger asChild>
							<MenuButtonComponent
								tooltip={item.title}
								size={depth === 0 ? "default" : "sm"}
								className={cn(
									"w-full justify-start",
									depth === 1 && "pl-4",
									depth === 2 && "pl-8",
									depth >= 3 && "pl-12",
									isActiveInTree && "bg-sidebar-accent text-sidebar-accent-foreground"
								)}
							>
								{Icon && <Icon className="h-4 w-4 shrink-0" />}
								<span className="truncate">{item.title}</span>
								<ChevronDown className={cn(
									"ml-auto h-4 w-4 shrink-0 transition-transform duration-200",
									"group-data-[state=open]/collapsible:rotate-180"
								)} />
							</MenuButtonComponent>
						</CollapsibleTrigger>
						<CollapsibleContent>
							<SidebarMenuSub>
								{filteredChildren.map((child, idx) => (
									<NavItem
										key={`${child.title}-${depth}-${idx}`}
										item={child}
										depth={depth + 1}
									/>
								))}
							</SidebarMenuSub>
						</CollapsibleContent>
					</MenuContainer>
				</Collapsible>
			</NavPermission>
		);
	}

	// For leaf items (no children) - handle proper nesting
	const MenuItemComponent = depth === 0 ? SidebarMenuItem : SidebarMenuSubItem;
	const MenuButtonComponent = depth === 0 ? SidebarMenuButton : SidebarMenuSubButton;
	const isActive = isActiveChecker(item.url ?? "#", currentPath);

	return (
		<NavPermission item={item}>
			<MenuItemComponent>
				<MenuButtonComponent
					asChild
					isActive={isActive}
					tooltip={item.title}
					size={depth === 0 ? "default" : "sm"}
					className={cn(
						"w-full justify-start",
						depth === 1 && "pl-4",
						depth === 2 && "pl-8",
						depth >= 3 && "pl-12",
						isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
					)}
				>
					<Link to={item.url ?? "#"}>
						{Icon && <Icon className="h-4 w-4 shrink-0" />}
						<span className="truncate">{item.title}</span>
						{item.new && (
							<span className="ml-auto shrink-0 rounded bg-sidebar-primary px-1.5 py-0.5 text-xs text-sidebar-primary-foreground">
								New
							</span>
						)}
					</Link>
				</MenuButtonComponent>
			</MenuItemComponent>
		</NavPermission>
	);
};

const NavItem = memo(NavItemBase);
